import { useState } from "react";
import { Box, Button, IconButton, TableCell, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import CustomTable from "../investorOverview/common/customTable";
import { TableHeaderData } from "../investorOverview/types";
import { MfuKycStatusObj } from "../../redux-store/types/api-types";
import { InfoIconOutlined } from "../../assets/SvgIcons";

const tableHeaderData: TableHeaderData[] = [
    {
        id: "id",
        label: "REFERENCE ID",
        align: "center",
        isSortable: false,
    },
    {
        id: "name",
        label: "INVESTOR NAME",
        align: "center",
        isSortable: false,
    },
    {
        id: "panNo",
        label: "INVESTOR PAN",
        align: "center",
        isSortable: false,
    },
    {
        id: "kraKYCStatus",
        label: "KRA KYC STATUS",
        align: "center",
        isSortable: false,
    },
    {
        id: "eventStatus",
        label: "EVENT STATUS",
        align: "center",
        isSortable: false,
    },
    {
        id: "action",
        label: "ACTION",
        align: "center",
        isSortable: false,
    },
];

interface KycTableBodyProps {
    tableData: MfuKycStatusObj[];
}

interface KycDetailsTableProps {
    isLoading: boolean;
    tableData: MfuKycStatusObj[];
    totalCount: number;
    currentPage: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function TooltipInfoItem({ label, value }: { label: string; value?: string | null }): JSX.Element {
    const theme = useTheme()

    return (
        <Box>
            <Typography
                variant='subtitle_500'
                sx={{
                    fontWeight: '600',
                    marginBottom: '5px',
                    color: theme.palette.primary[500]
                }}
            >
                {label}
            </Typography>
            <Typography variant='subtitle_600'>
                {value ?? "-"}
            </Typography>
        </Box>
    )
}

function EventStatusTooltip({ rowData }: { rowData: MfuKycStatusObj }): JSX.Element {
    const theme = useTheme()

    const tooltipItems: Array<{ label: string; value?: string | null }> = [
        { label: "RESIDENCE STATUS", value: rowData?.residenceStatus },
        { label: "IS DIGILOCKER SELECTED", value: rowData?.isDigiLocker },
        { label: "DIGILOCKER STATUS", value: rowData?.digiLockerStatus },
        { label: "WET SIGNATURE STATUS", value: rowData?.wetSignatureStatus },
        { label: "ESIGN STATUS", value: rowData?.esignStatus },
        { label: "MANUAL KYC UPDATED", value: rowData?.manualKycUpdated },
        { label: "VIDEO IPV UPLOADED", value: rowData?.videoIpvUploaded },
        { label: "KRA ENTRY DATE", value: rowData?.kraEntryDate },
        { label: "KRA SUBMISSION STATUS", value: rowData?.kraSubmissionStatus },
        { label: "KRA REJECTION REASON", value: rowData?.kraRejectionReason },
    ]

    return (
        <Tooltip
            slotProps={{
                tooltip: {
                    sx: {
                        margin: "0px !important",
                        backgroundColor: "transparent"
                    }
                }
            }}
            title={
                <Box
                    sx={{
                        borderRadius: '10px',
                        backgroundColor: theme.palette.text.primary,
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '10px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    {tooltipItems.map((item) => (
                        <TooltipInfoItem
                            key={item.label}
                            label={item.label}
                            value={item.value}
                        />
                    ))}
                </Box>
            }
        >
            <IconButton>
                <InfoIconOutlined />
            </IconButton>
        </Tooltip>
    )
}

function KycTableBody({ tableData }: KycTableBodyProps): JSX.Element {
    const theme = useTheme();
    const [copiedIds, setCopiedIds] = useState<Set<number>>(new Set());

    const handleCopyLink = async (url: string, id: number) => {
        if (url) {
            await navigator.clipboard.writeText(url);
            setCopiedIds((prevState) => new Set(prevState).add(id));
            setTimeout(() => {
                setCopiedIds((prevState) => {
                    const modifiedSet = new Set(prevState);
                    modifiedSet.delete(id);
                    return modifiedSet;
                });
            }, 1500);
        }
    };

    return (
        <>
            {tableData.map((row: MfuKycStatusObj) => {
                return (
                    <TableRow key={row.id}>
                        <TableCell align="center" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}
                            >
                                {row?.id ?? '-'}
                            </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}
                            >
                                {row?.name ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.text.primary, wordBreak: 'break-word' }}
                            >
                                {row?.panNo ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                            <Typography
                                variant="body_500"
                                sx={{ color: theme.palette.primary[500], wordBreak: 'break-word' }}
                            >
                                {row?.kraKYCStatus ?? "-"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <EventStatusTooltip rowData={row} />
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Button
                                variant="text"
                                disabled={copiedIds.has(row.id)}
                                onClick={() => handleCopyLink(row?.KYCOnboardingShortUrl ?? row?.KYCOnboardingUrl ?? "", row.id)}
                                sx={{
                                    ...theme.typography.body_500,
                                    color: theme.palette.primary[500],
                                    textTransform: "none",
                                    padding: "5px 10px",
                                    minWidth: "90px",
                                    "&.Mui-disabled": {
                                        color: theme.palette.primary[500]
                                    },
                                }}
                            >
                                {(row?.KYCOnboardingShortUrl ?? row?.KYCOnboardingUrl ?? "") ? (copiedIds.has(row.id) ? "Copied" : "Copy Link") : "-"}
                            </Button>
                        </TableCell>
                    </TableRow>
                );
            })}
        </>
    );
}

export default function KycDetailsTable({
    isLoading,
    tableData,
    totalCount,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}: KycDetailsTableProps): JSX.Element {
    return (
        <CustomTable
            isLoading={isLoading}
            tableHeaderData={tableHeaderData}
            tableBody={
                <KycTableBody
                    tableData={tableData}
                />
            }
            tableData={tableData}
            tableDataCount={totalCount}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handlePageChange={onPageChange}
            handleRowsPerPageChange={onRowsPerPageChange}
            loadingRowsNum={10}
            tableBodyCustomStyles={{
                '& tr:last-child td': {
                    border: 0,
                },
            }}
        />
    );
}
